// Agent Marketing - Resources / Playbook

export const resourceCategories = [
  "First Impressions",
  "Google Presence",
  "Websites",
  "Listing Marketing",
  "Social Media",
  "AI For Agents",
  "CRM And Automation",
  "Reviews",
  "Referral Systems",
  "Paid Advertising",
  "SEO And AI Visibility",
  "Recruiting And Agency Growth",
  "Print And Offline Branding",
];

export const articles = [
  {
    slug: "why-clients-search-you-before-they-call",
    category: "First Impressions",
    title: "Why Clients Search You Before They Call",
    subtitle: "The first conversation almost always begins online.",
    readTime: "4 min read",
    excerpt: "Prospects, referral partners, and past clients look you up before they ever speak with you. What they find sets the tone for the entire relationship.",
    body: [
      "Almost every serious inquiry starts with a search. A friend recommends you. A referral partner passes along your name. A past client mentions you in a group chat. In each of those moments, the very next step is not a phone call. It is a search.",
      "That search is short, quiet, and decisive. It happens on a phone, on the couch, during lunch, or between other errands. It rarely takes more than a minute. But in that minute, the prospect decides whether you feel credible.",
      "If they find a clear website, a real Google Business Profile, a branded email address, and consistent social presence, they lean in. The conversation you have next feels almost pre-approved.",
      "If they find a broken link, a template that looks like everyone else's, or a Gmail address on a business card, they lean back. They still might call. But they call with more hesitation and less trust.",
      "This is why professional presence is not a vanity project. It is the ambient condition of every client relationship. Your presence is the room the prospect walks into before they meet you.",
      "Agent Marketing exists to make sure that room looks the way your business deserves.",
    ],
  },
  {
    slug: "why-referrals-still-research-you",
    category: "Referral Systems",
    title: "Why Referrals Still Research You",
    subtitle: "A warm intro is not the same as a closed deal.",
    readTime: "3 min read",
    excerpt: "Even referred prospects look you up before they respond. What they find can either confirm the referral or quietly undermine it.",
    body: [
      "A common assumption is that referrals arrive ready to work. In practice, most referred prospects still do a background check. They search your name, glance at your website, check your Google reviews, and scan your social presence.",
      "This is not skepticism. It is normal. People want to feel prepared before an important conversation.",
      "If your online presence supports the referral, the introduction closes on its own. If your presence contradicts the referral, the prospect feels quiet doubt and often does not follow up.",
      "This is why referral partners naturally send more business to professionals whose online presence backs them up. Referral sources protect their own reputation. They send business to people they are confident will make them look good.",
      "The takeaway is simple. If you want more referrals, make yourself easier to refer.",
    ],
  },
  {
    slug: "hidden-cost-of-diy-marketing",
    category: "First Impressions",
    title: "The Hidden Cost Of DIY Marketing For Agents",
    subtitle: "The real price is rarely the software subscription.",
    readTime: "5 min read",
    excerpt: "DIY marketing can feel cheaper at first. The cost usually shows up later in trust, hesitation, and missed appointments.",
    body: [
      "The most expensive marketing is not the marketing you paid for. It is the marketing you built yourself between appointments, late at night, or inside tools you never had time to learn properly.",
      "The direct cost of DIY looks small. A few subscriptions, a domain, some templates. The indirect cost is much larger. It shows up as hours pulled away from prospecting, follow-up, and client work. It shows up as inconsistency across your assets. It shows up as the prospect who liked you but chose someone who looked more prepared.",
      "This is not a criticism of DIY. Many good businesses start there. The problem begins when the business starts depending on assets that were never built to hold that weight.",
      "A useful test is this: if a prospect landed on every one of your public assets today, would they feel that they were dealing with an established professional or a work in progress?",
      "The answer to that question is worth measuring in commissions, not in software subscriptions.",
    ],
  },
  {
    slug: "professional-email-still-matters",
    category: "First Impressions",
    title: "Why A Professional Email Address Still Matters",
    subtitle: "Small signal. Big consequences.",
    readTime: "3 min read",
    excerpt: "A branded email address is a quiet signal. So is a Gmail address. Both say something about how established the business behind them really is.",
    body: [
      "Email is not the most exciting part of a brand system. It is one of the most read.",
      "Every message you send is a small trust event. A branded email on your own domain reinforces the idea that there is a real business behind the person. A generic email address does the opposite, even when the person on the other end is more capable than most of their competitors.",
      "The fix is simple, cheap, and one of the highest leverage moves a professional can make. Register the domain. Set up branded email accounts. Design signatures that carry the brand forward.",
      "It is a small change. It is also one of the fastest ways to look more established.",
    ],
  },
  {
    slug: "what-makes-an-agent-look-established",
    category: "First Impressions",
    title: "What Makes An Agent Look Established Online",
    subtitle: "Established is a feeling, not a job title.",
    readTime: "4 min read",
    excerpt: "The difference between looking established and looking new usually comes down to five or six small signals that stack together.",
    body: [
      "New agents often assume that looking established requires years of experience. Established professionals often assume that their track record speaks for itself. Both are wrong.",
      "Looking established is about signal density. It is the compound effect of consistent branding, a clear website, a complete Google profile, a branded email address, active social presence, and organized listing materials.",
      "None of these signals is loud. Together, they form a quiet impression that the business is real, organized, and here to stay.",
      "The good news is that all of these signals are buildable. You do not need decades in the industry to look serious. You need a system.",
    ],
  },
  {
    slug: "listing-graphics-brand-system",
    category: "Listing Marketing",
    title: "Why Listing Graphics Should Follow A Brand System",
    subtitle: "The goal is not to make every listing look different.",
    readTime: "4 min read",
    excerpt: "The goal is to make every listing look like it came from you. That is what turns marketing into brand memory.",
    body: [
      "It is tempting to make every listing feel unique. New fonts, new colours, new layouts, new styles. AI tools make this easier than ever.",
      "The problem is that a market full of one-off graphics does not build brand memory. It builds noise. People see the property but they do not remember who marketed it.",
      "A branded listing template system does the opposite. It gives every property the same visual language. Different addresses, different prices, different photos, but the same recognizable frame.",
      "Over time, that frame becomes the brand. People stop remembering individual listings and start remembering that all of them came from you. That is when marketing starts compounding.",
    ],
  },
  {
    slug: "google-business-profiles-local-trust",
    category: "Google Presence",
    title: "How Google Business Profiles Build Local Trust",
    subtitle: "Often the first result. Usually underused.",
    readTime: "4 min read",
    excerpt: "A complete Google Business Profile answers questions before the prospect ever calls, and shapes trust before they visit the website.",
    body: [
      "For local professional services, the Google Business Profile is often the first result a prospect sees. It usually shows up above the website, above the social feed, and above the paid ads.",
      "That real estate matters. A complete profile answers the basic questions a prospect has before they call. Where do you serve? What do you do? What do previous clients say? How do you contact them?",
      "An incomplete or missing profile forces prospects to do extra work. Most will not. They will click on the next result that answers those questions for them.",
      "Setting up and optimizing a Google Business Profile is one of the highest leverage marketing moves a local professional can make. It is also one of the most overlooked.",
    ],
  },
  {
    slug: "social-consistency-over-volume",
    category: "Social Media",
    title: "Why Social Media Consistency Matters More Than Volume",
    subtitle: "Fewer posts, one voice.",
    readTime: "4 min read",
    excerpt: "You do not need to post every day. You need to post like the same business every time.",
    body: [
      "Most professionals feel guilty about not posting enough. The truth is that volume is not the main problem. Consistency is.",
      "A feed with three posts a month that all look like they came from the same business builds more trust than a feed with twelve posts a month that look like they came from twelve different businesses.",
      "Consistency is what tells the visitor that there is a real operation behind the account. That signal outperforms almost every posting frequency debate.",
      "Start with a brand system. Post inside it. Let volume grow later.",
    ],
  },
];

export const findArticle = (slug) => articles.find((a) => a.slug === slug);
